import { Injectable, inject, Signal } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  addWeeks,
  endOfISOWeek,
  format,
  startOfISOWeek,
  subWeeks,
} from 'date-fns';
import { catchError, exhaustMap, filter, map, of } from 'rxjs';
import { TaskService } from '../../task.service';
import { PlanFacade } from '../plan/plan.facade';
import { TaskActions, TaskPageActions } from './task.actions';
import { PlanModuleActions, PlanPageActions } from '../plan/plan.actions';
import { getNewPosition, getParentTaskId } from '@lyceo/shared/plan/util';
import { MoveTaskRequest, Task } from '@lyceo/shared/plan/util-models';
import { TaskFacade } from './task.facade';
import { ApplicationFacade, selectUrl } from '@lyceo/shared/data-access';
import { isDefined } from '@lyceo/shared/util';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslocoService } from '@jsverse/transloco';

@Injectable()
export class TaskEffects {
  private readonly actions$ = inject(Actions);
  private readonly taskService: TaskService = inject(TaskService);
  private readonly planFacade: PlanFacade = inject(PlanFacade);
  private readonly taskFacade: TaskFacade = inject(TaskFacade);
  private readonly modalController: ModalController = inject(ModalController);
  private readonly applicationFacade: ApplicationFacade =
    inject(ApplicationFacade);
  private readonly router: Router = inject(Router);
  private readonly store: Store = inject(Store);
  private readonly translocoService: TranslocoService =
    inject(TranslocoService);

  public create$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TaskActions.create),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.createFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService.create(pupilId, action.request).pipe(
          map((task) => TaskActions.createSuccess({ task: task })),
          catchError((error: Error) =>
            of(TaskActions.createFailure({ error })),
          ),
        );
      }),
    );
  });

  public update$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TaskActions.update),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.updateFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService.update(pupilId, action.request).pipe(
          map((task) => TaskActions.updateSuccess({ task: task })),
          catchError((error: Error) =>
            of(TaskActions.updateFailure({ error })),
          ),
        );
      }),
    );
  });

  public failure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          TaskActions.createFailure,
          TaskActions.updateFailure,
          TaskActions.updatePupilDoneFailure,
        ),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.task_save_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public successTask$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskActions.createSuccess, TaskActions.updateSuccess),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'success',
            message: this.translocoService.translate(
              'plan.toasts.task_save_success',
            ),
          });

          this.modalController.dismiss();
        }),
      );
    },
    { dispatch: false },
  );

  public updatePupilDone$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter((action) => !action.value || !action.task.parentTaskId),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.updatePupilDoneFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService
          .updatePupilDone(pupilId, {
            id: action.task.id,
            pupilDone: action.value,
          })
          .pipe(
            map((task) => TaskActions.updatePupilDoneSuccess({ task: task })),
            catchError((error: Error) =>
              of(TaskActions.updatePupilDoneFailure({ error })),
            ),
          );
      }),
    );
  });

  public updatePupilDoneNoReflection$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.pupilCompleteTaskClicked),
      filter(
        (action) =>
          action.value && !!action.task.parentTaskId && !action.showReflection,
      ),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.updatePupilDoneFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService
          .updatePupilDone(pupilId, {
            id: action.task.id,
            pupilDone: action.value,
          })
          .pipe(
            map((task) => TaskActions.updatePupilDoneSuccess({ task: task })),
            catchError((error: Error) =>
              of(TaskActions.updatePupilDoneFailure({ error })),
            ),
          );
      }),
    );
  });

  public updateDoneAt$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.instructorCompleteTaskClicked),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.updateDoneAtFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService
          .updateDoneAt(pupilId, {
            id: action.task.id,
            done: action.value,
          })
          .pipe(
            map((task) => TaskActions.updateDoneAtSuccess({ task: task })),
            catchError((error: Error) =>
              of(TaskActions.updateDoneAtFailure({ error })),
            ),
          );
      }),
    );
  });

  public getTask$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PlanPageActions.dayListTaskClicked,
        PlanPageActions.calendarTaskClicked,
        PlanPageActions.todoTaskClicked,
        TaskPageActions.todoTaskClicked,
      ),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.getFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService.get(pupilId, getParentTaskId(action.task)).pipe(
          map((task) => TaskActions.getSuccess({ task: task })),
          catchError((error: Error) => of(TaskActions.getFailure({ error }))),
        );
      }),
    );
  });

  public delete$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(TaskActions.delete),
      exhaustMap((action) => {
        const pupilId: string | undefined = this.planFacade.pupilId();
        if (!isDefined(pupilId)) {
          return of(
            TaskActions.deleteFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        return this.taskService.delete(pupilId, action.id).pipe(
          map(() => TaskActions.deleteSuccess({ id: action.id })),
          catchError((error: Error) =>
            of(TaskActions.deleteFailure({ error })),
          ),
        );
      }),
    );
  });

  public deleteFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskActions.deleteFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.task_delete_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public fetchTasksBySelectedDate$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PlanModuleActions.pupilContextChanged,
        PlanPageActions.calendarDayClicked,
      ),
      map(() => this.planFacade.pupilId()),
      filter(isDefined),
      exhaustMap((pupilId) =>
        this.taskService
          .fetchByDate(pupilId, this.planFacade.selectedDate())
          .pipe(
            map((tasks) => TaskActions.fetchByDateSuccess({ tasks: tasks })),
            catchError((error: Error) =>
              of(TaskActions.fetchByDateFailure({ error })),
            ),
          ),
      ),
    );
  });

  public fetchCalendar$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanModuleActions.pupilContextChanged),
      map(() => this.planFacade.pupilId()),
      filter(isDefined),
      exhaustMap((pupilId) =>
        this.taskService
          .fetchByRange(
            pupilId,
            startOfISOWeek(this.planFacade.activeDate()),
            endOfISOWeek(
              addWeeks(
                this.planFacade.activeDate(),
                this.planFacade.numberOfWeeks() - 1,
              ),
            ),
          )
          .pipe(
            map((tasks) => TaskActions.fetchCalendarSuccess({ tasks: tasks })),
            catchError((error: Error) =>
              of(TaskActions.fetchCalendarFailure({ error })),
            ),
          ),
      ),
    );
  });

  public prefetchCalendarPrevious$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        TaskActions.prefetchCalendarPrevious,
        TaskActions.fetchCalendarSuccess,
      ),
      map(() => this.planFacade.pupilId()),
      filter(isDefined),
      exhaustMap((pupilId) =>
        this.taskService
          .fetchByRange(
            pupilId,
            startOfISOWeek(
              subWeeks(
                this.planFacade.activeDate(),
                this.planFacade.numberOfWeeks(),
              ),
            ),
            endOfISOWeek(subWeeks(this.planFacade.activeDate(), 1)),
          )
          .pipe(
            map((tasks) =>
              TaskActions.prefetchCalendarPreviousSuccess({ tasks: tasks }),
            ),
            catchError((error: Error) =>
              of(TaskActions.prefetchCalendarPreviousFailure({ error })),
            ),
          ),
      ),
    );
  });

  public prefetchCalendarNext$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        TaskActions.prefetchCalendarNext,
        TaskActions.fetchCalendarSuccess,
      ),
      map(() => this.planFacade.pupilId()),
      filter(isDefined),
      exhaustMap((pupilId) =>
        this.taskService
          .fetchByRange(
            pupilId,
            startOfISOWeek(
              addWeeks(
                this.planFacade.activeDate(),
                this.planFacade.numberOfWeeks(),
              ),
            ),
            endOfISOWeek(
              addWeeks(
                this.planFacade.activeDate(),
                this.planFacade.numberOfWeeks() * 2 - 1,
              ),
            ),
          )
          .pipe(
            map((tasks) =>
              TaskActions.prefetchCalendarNextSuccess({ tasks: tasks }),
            ),
            catchError((error: Error) =>
              of(TaskActions.prefetchCalendarNextFailure({ error })),
            ),
          ),
      ),
    );
  });

  public fetchTasksFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(
          TaskActions.fetchByDateFailure,
          TaskActions.fetchCalendarFailure,
        ),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.task_load_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public getTodoTasks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(
        PlanModuleActions.pupilContextChanged,
        TaskPageActions.init,
        TaskActions.updatePupilDoneSuccess,
        TaskActions.createSuccess,
        TaskActions.updateSuccess,
        TaskActions.deleteSuccess,
      ),
      map(() => this.planFacade.pupilId()),
      filter(isDefined),
      exhaustMap((pupilId) =>
        this.taskService.getTodoTasks(pupilId).pipe(
          map((tasks) => TaskActions.getTodoTasksSuccess({ tasks: tasks })),
          catchError((error: Error) =>
            of(TaskActions.getTodoTasksFailure({ error })),
          ),
        ),
      ),
    );
  });

  public getTodoTasksFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskActions.getTodoTasksFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.todo_task_load_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public taskMoved$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(PlanPageActions.taskMoved),
      exhaustMap((action) => {
        const sorted: Task[] = this.taskFacade.sortedTasksByDay(
          action.moved.day,
        )();
        const pupilId: string | undefined = this.planFacade.pupilId();

        if (!isDefined(pupilId)) {
          return of(
            TaskActions.taskMovedFailure({
              error: new Error('PupilId is not defined'),
            }),
          );
        }

        const task: Task = action.moved.event.meta.task;
        const request: MoveTaskRequest = {
          id: task.id,
          scheduledAt: format(action.moved.day, 'yyyy-MM-dd'),
          position: getNewPosition(action.moved.index, task, sorted),
        };
        return this.taskService.move(pupilId, request).pipe(
          map((result) => TaskActions.taskMovedSuccess({ tasks: result })),
          catchError((error: Error) =>
            of(TaskActions.taskMovedFailure({ error })),
          ),
        );
      }),
    );
  });

  public taskMovedSuccess$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskActions.taskMovedSuccess),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'success',
            message: this.translocoService.translate(
              'plan.toasts.task_move_success',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public taskMovedFailure$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(TaskActions.taskMovedFailure),
        map(() => {
          this.applicationFacade.showToaster({
            type: 'error',
            message: this.translocoService.translate(
              'plan.toasts.task_move_failure',
            ),
          });
        }),
      );
    },
    { dispatch: false },
  );

  public redirectNoTodoTasks$ = createEffect(
    () => {
      const routeUrl: Signal<string> = this.store.selectSignal(selectUrl);
      return this.actions$.pipe(
        ofType(TaskActions.getTodoTasksSuccess),
        filter(() => {
          return (
            this.taskFacade.allTodoTasks().length === 0 &&
            routeUrl().includes('/plan/tasks')
          );
        }),
        map(() => {
          this.router.navigate(['/plan']);
        }),
      );
    },
    { dispatch: false },
  );
}
